/**
 * ACIL Intelligence - Login Page
 */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../utils/api';
import { Shield, Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react';
import '../App.css';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password, remember);
      if (data.status === 'success') {
        navigate('/dashboard');
      } else if (data.status === '2fa_setup_required') {
        navigate('/auth/setup-2fa');
      } else if (data.status === '2fa_required') {
        navigate('/auth/verify-2fa');
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Error de conexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="login-page">
      <div className="auth-card">
        {/* Logo */}
        <div className="auth-logo">
          <div className="auth-logo-icon">
            <Shield size={22} />
          </div>
          <span className="auth-logo-text">ACIL Intelligence</span>
        </div>

        {/* Badge */}
        <div style={{ marginBottom: 24 }}>
          <span className="badge-live" data-testid="secure-badge">Acceso seguro</span>
        </div>

        {/* Title */}
        <h1 className="auth-title">Bienvenido de nuevo</h1>
        <p className="auth-subtitle">
          Ingresa tus credenciales para acceder a la plataforma de inteligencia operativa.
        </p>

        {/* Error */}
        {error && (
          <div className="auth-error-banner" data-testid="login-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} data-testid="login-form">
          <div style={{ marginBottom: 16 }}>
            <label className="acil-label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className="acil-input"
              placeholder="tu@acilmexico.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="login-email-input"
            />
          </div>

          <div style={{ marginBottom: 16, position: 'relative' }}>
            <label className="acil-label" htmlFor="password">Contraseña</label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPwd ? 'text' : 'password'}
                className="acil-input"
                placeholder="Ingresa tu contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                style={{ paddingRight: 48 }}
                data-testid="login-password-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPwd(!showPwd)}
                tabIndex={-1}
                data-testid="toggle-password-visibility"
              >
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--muted)' }}>
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                style={{ accentColor: 'var(--orange)' }}
                data-testid="remember-me-checkbox"
              />
              Recordarme
            </label>
            <Link
              to="/auth/forgot-password"
              style={{ fontSize: 13, color: 'var(--orange)', fontWeight: 600, textDecoration: 'none' }}
              data-testid="forgot-password-link"
            >
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
            style={{ width: '100%', height: 48 }}
            data-testid="login-submit-button"
          >
            {loading ? (
              <><div className="acil-spinner" /> Iniciando sesión...</>
            ) : (
              'Iniciar sesión'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
